import { FormRoot } from "src/components/FormV2/FormRoot.tsx";
import { FormInner } from "src/components/FormV2/FormInner.tsx";
import { FormGrid } from "src/components/FormV2/FormGrid.tsx";
import { FormColumn } from "src/components/FormV2/FormColumn.tsx";
import { FormContext } from "src/components/FormV2/FormContext.tsx";
import {
  forwardRef,
  type HTMLAttributes,
  type PropsWithChildren,
  type UIEvent,
  useMemo,
  useRef,
} from "react";
import { formClasses } from "src/components/FormV2/Form.constants.ts";
import { composeRefs, useControlled } from "@exonn/hooks";
import { useFormContext } from "src/components/FormV2/useFormContext.ts";
import { FormToolbar } from "src/components/FormV2/FormToolbar/FormToolbar.tsx";
import { FormFooter } from "src/components/FormV2/FormFooter.tsx";

type RootProps = PropsWithChildren<HTMLAttributes<HTMLFormElement>> & {
  onHasScrolled?: (state: boolean) => void;
  hasScrolled?: boolean;
  defaultHasScrolled?: boolean;
  disableToolbarAnimation?: boolean;
  defaultDisableToolbarAnimation?: boolean;
  onDisableToolbarAnimation?: (state: boolean) => void;
};
const Root = ({
  children,
  onHasScrolled,
  hasScrolled: hasScrolledProp,
  defaultHasScrolled = false,
  disableToolbarAnimation: disableToolbarAnimationProp,
  defaultDisableToolbarAnimation = true,
  onDisableToolbarAnimation,
  ...restProps
}: RootProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [hasScrolled, setHasScrolled] = useControlled({
    state: "hasScrolled",
    name: "FormV2.Root",
    controlled: hasScrolledProp,
    default: defaultHasScrolled,
    onChange: onHasScrolled,
  });
  // I don't want to animate toolbar on first render when scroll position is restored.
  // this animation is annoying
  // I want to animate toolbar only on scroll
  const [disableToolbarAnimation, setDisableToolbarAnimation] = useControlled({
    state: "disableToolbarAnimation",
    name: "FormV2.Root",
    controlled: disableToolbarAnimationProp,
    default: defaultDisableToolbarAnimation,
    onChange: onDisableToolbarAnimation,
  });

  const context = useMemo(
    () => ({
      hasScrolled,
      setHasScrolled,
      disableToolbarAnimation,
      scrollContainerRef,
      setDisableToolbarAnimation,
    }),
    [
      hasScrolled,
      setHasScrolled,
      disableToolbarAnimation,
      setDisableToolbarAnimation,
    ],
  );
  return (
    <FormContext.Provider value={context}>
      <FormRoot className={formClasses.root} {...restProps}>
        {children}
      </FormRoot>
    </FormContext.Provider>
  );
};

const Inner = forwardRef(
  (
    {
      children,
      ...restProps
    }: PropsWithChildren<HTMLAttributes<HTMLDivElement>>,
    ref,
  ) => {
    const { scrollContainerRef, setHasScrolled, setDisableToolbarAnimation } =
      useFormContext();
    const handleScroll = (e: UIEvent<HTMLDivElement>) => {
      setDisableToolbarAnimation(false);
      setHasScrolled(e.currentTarget.scrollTop > 0);
    };

    const checkHasScrolled = (node: HTMLDivElement | null) => {
      if (node) {
        setHasScrolled(node.scrollTop > 0);
      }
    };
    return (
      <FormInner
        className={formClasses.inner}
        onScroll={handleScroll}
        ref={composeRefs(scrollContainerRef, ref, checkHasScrolled)}
        {...restProps}
      >
        {children}
      </FormInner>
    );
  },
);

const Grid = ({
  children,
  ...restProps
}: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) => {
  return (
    <FormGrid className={formClasses.grid} {...restProps}>
      {children}
    </FormGrid>
  );
};

const Footer = ({
  children,
  ...restProps
}: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) => {
  return <FormFooter {...restProps}>{children}</FormFooter>;
};

export const FormV2 = Object.assign(FormRoot, {
  Root: Root,
  Inner: Inner,
  Grid: Grid,
  Column: FormColumn,
  Toolbar: FormToolbar,
  Footer: Footer,
});
